import { supabase } from './supabase';
import { Message, SearchData } from '@/types/chat';

export interface CourseRequestData {
  email: string;
  searchData: SearchData | null;
  conversationHistory: Message[];
  conversationId: string | null;
}

// ✅ FIX #9 — Limit conversation history to prevent huge payloads (Supabase rejects >1MB)
const MAX_HISTORY_MESSAGES = 50;

class CourseRequestService {
  /**
   * Save a course request when no courses are found
   */
  async saveCourseRequest(data: CourseRequestData): Promise<{ success: boolean; error?: string }> {
    try {
      console.log('[CourseRequest] 💾 Saving course request for email:', data.email);

      // Validate email format
      const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
      if (!emailRegex.test(data.email)) {
        return {
          success: false,
          error: 'כתובת מייל לא תקינה',
        };
      }

      // ✅ FIX #9 — Truncate history to last MAX_HISTORY_MESSAGES to prevent payload bloat
      const recentHistory = data.conversationHistory.slice(-MAX_HISTORY_MESSAGES);

      if (data.conversationHistory.length > MAX_HISTORY_MESSAGES) {
        console.warn(
          `[CourseRequest] ⚠️ Trimmed conversation history from ${data.conversationHistory.length} to ${MAX_HISTORY_MESSAGES} messages`
        );
      }

      // Prepare the data for insertion
      const requestData = {
        email: data.email.trim().toLowerCase(),
        search_data: data.searchData || {},
        conversation_history: recentHistory.map((msg) => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp,
        })),
        conversation_id: data.conversationId,
        status: 'pending',
      };

      console.log('[CourseRequest] 📝 Request data prepared:', {
        email: requestData.email,
        hasSearchData: !!data.searchData,
        messageCount: requestData.conversation_history.length,
        originalMessageCount: data.conversationHistory.length,
        hasConversationId: !!data.conversationId,
      });

      // Insert into database
      const { data: insertedData, error } = await supabase
        .from('course_requests')
        .insert(requestData)
        .select('id')
        .single();

      if (error) {
        console.error('[CourseRequest] ❌ Error saving request:', error);

        // ✅ FIX — surface a more helpful error if the table doesn't exist
        if (error.code === '42P01') {
          return {
            success: false,
            error: 'שגיאת הגדרה: טבלת הבקשות לא נמצאה. אנא פנה לתמיכה.',
          };
        }

        return {
          success: false,
          error: 'שגיאה בשמירת הבקשה. אנא נסה שוב.',
        };
      }

      console.log('[CourseRequest] ✅ Request saved successfully:', insertedData.id);
      return { success: true };
    } catch (error) {
      console.error('[CourseRequest] ❌ Exception saving request:', error);
      return {
        success: false,
        error: 'שגיאה בשמירת הבקשה. אנא נסה שוב.',
      };
    }
  }

  /**
   * Check if email was already submitted for this conversation
   */
  async hasEmailForConversation(conversationId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from('course_requests')
        .select('id')
        .eq('conversation_id', conversationId)
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('[CourseRequest] ❌ Error checking existing email:', error);
        return false;
      }

      return !!data;
    } catch (error) {
      console.error('[CourseRequest] ❌ Exception checking existing email:', error);
      return false;
    }
  }
}

export const courseRequestService = new CourseRequestService();